import api from '../api';
import { newUuid } from '../collectionRecord';
import { sha256Hex } from '../sha256';
import { failureOf, putObject } from '../uploadPhotos';

/*
 * Importing a radiometric file (docs/FIELD_AI_VISION_SPEC.md sections 38-41),
 * shared by the dossier's Thermal strip and Field Mode's outbox.
 *
 * THE FILE GOES UP BYTE FOR BYTE. Re-encoding destroys the radiometric data,
 * so nothing here reads it as a picture: it is hashed as it is, PUT as it is
 * to the address POST /thermal/uploads presigns (thermal/<sha256>/original),
 * and registered with POST /thermal/captures, where the server GETs it back,
 * checks the hash and runs the decoder factory.
 *
 * The same file twice is one capture: an upload of bytes the server already
 * holds gets no address, and registering it again only adds the links. So a
 * retry after a lost answer is always safe, with no idempotency key.
 *
 * A file no decoder reads is still a capture, kept 'undecoded' with the
 * detection report (a 422 whose body carries it): it went up, and can be
 * decoded later with another format. That is a success here, `decoded: false`.
 *
 *   uploadThermal(blob, {contentType, links, lidState, format})
 *     -> {ok: true, capture, decoded, refusal}     refusal: {code, error} or null
 *     -> {ok: false, status, code, unreachable}    nothing was registered
 */
export const THERMAL_MAX_BYTES = 25 * 1024 * 1024;
export const THERMAL_TYPES = Object.freeze({ 'image/jpeg': 'jpg', 'image/tiff': 'tif' });
const BY_EXTENSION = { jpg: 'image/jpeg', jpeg: 'image/jpeg', tif: 'image/tiff', tiff: 'image/tiff' };
/** What a file picker offers: the vendors' radiometric JPEGs and TIFFs. */
export const THERMAL_ACCEPT = 'image/jpeg,image/tiff,.jpg,.jpeg,.tif,.tiff';

/** image/jpeg or image/tiff, by the file's type or else its name; '' for anything else. */
export function thermalContentType(file) {
  const type = String(file?.type || '').split(';')[0].trim().toLowerCase();
  if (THERMAL_TYPES[type]) return type;
  const name = String(file?.name || '');
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return BY_EXTENSION[ext] || '';
}

/** Why a file cannot be imported ('type', 'empty' or 'size'), or null. */
export function thermalFileProblem(file) {
  if (!thermalContentType(file)) return 'type';
  if (!file.size) return 'empty';
  if (file.size > THERMAL_MAX_BYTES) return 'size';
  return null;
}

/** A thermal file waiting on a Field batch; its bytes are put in the store under `id`. */
export function newBatchThermal({ id = newUuid(), name = '', size = 0, contentType, addedAt = null, lidState = null }) {
  return { id, name: String(name || '').slice(0, 200), size, contentType, addedAt, lidState, sent: false };
}

export async function uploadThermal(blob, {
  contentType = thermalContentType(blob), links = [], lidState = null, format = 'auto', requestConfig,
} = {}) {
  const config = requestConfig ? [requestConfig] : [];
  try {
    const buffer = await blob.arrayBuffer();
    const sha = await sha256Hex(buffer);
    const issued = (await api.post('/thermal/uploads', {
      sha256: sha, content_type: contentType, bytes: buffer.byteLength,
    }, ...config)).data;
    if (!issued.exists) await putObject(issued.presign_url, blob, contentType);
    let res;
    try {
      res = await api.post('/thermal/captures', {
        sha256: sha, content_type: contentType, format, links, ...(lidState ? { lid_state: lidState } : {}),
      }, ...config);
    } catch (e) {
      // Kept, but no decoder read it: still a capture, with its refusal.
      const body = e?.response?.data;
      if (e?.response?.status === 422 && body?.capture) {
        return { ok: true, capture: body.capture, decoded: false, refusal: { code: body.code, error: body.error } };
      }
      throw e;
    }
    return { ok: true, capture: res.data, decoded: res.data?.status === 'decoded', refusal: null };
  } catch (e) {
    return { ok: false, ...failureOf(e) };
  }
}
